import * as Sentry from '@sentry/react';
import queryString from 'query-string';
import React from 'react';
import {Navigate, Route, Routes, useLocation, useParams} from 'react-router-dom';

import Dashboard from 'app/components/Dashboard';
import ErrorPages from 'app/components/Error';
import InternalTools from 'app/components/InternalTools/InternalTools';
import {LayoutWrapper} from 'app/components/LayoutWrapper';
import Loading from 'app/components/Loading';
import RequireRoleContainer from 'app/components/RequireRoleContainer';
import SignOut from 'app/components/SignOut/view';
import NewCustomerSignUpSuccess from 'app/components/SignUp/SignupSuccess';
import {WorkflowsProvider} from 'app/pages/Agent/WorkflowsProvider';
import CartPage from 'app/pages/Cart';
import LensHome from 'app/pages/Home';
import ProjectDashboardSwitch from 'app/pages/ProjectDashboardSwitch';
import {WithAuth, WithLoggedInAuth, WithLoggedOutAuth} from 'app/providers/AuthProvider';
import {getApiRoute} from 'app/utils/apiUtils';
import {loginAppUrl} from 'app/utils/environmentUtils';
import featureFlags, {isLensPlaygroundUser} from 'app/utils/featureFlags';
import * as routeUtils from 'app/utils/routeUtils';
import ScrollOnRouteChange from 'app/utils/ScrollOnRouteChange';
import * as userUtils from 'app/utils/userUtils';

import PublicWidget from './components/Dashboard/PublicWidget';
import GlobalSettings, {isGlobalSettingsView} from './components/GlobalSettings/GlobalSettings';
import PublicMap from './components/PublicMap';
import PublicReportPage from './components/PublicReport/PublicReportPage';
import PublicStoryPage from './components/PublicStory/PublicStoryPage';
import WorkflowsPage from './pages/Agent/AgentWorkflows';
import {AgentWorkflowSwitch} from './pages/Agent/AgentWorkflowSwitch';
import AgentChatPage from './pages/AgentChat';
import ManageFeatures, {
  ManagePropertiesMode,
  isManagePropertyModeOption,
} from './pages/ManageProperties';
import {OnboardingSlidesView} from './pages/OnboardingSlidesView';
import OrganizationSwitch from './pages/OrganizationSwitch';
import PropertyUpdate from './pages/PropertyUpdate';
import StoryViewPage from './pages/StoryView';
// Lazy-loaded: the SignUp module's top-level `loadStripe()` call fetches
// js.stripe.com, so we only want it triggered for users actually on /signup,
// not on every page load.
const NewCustomerSignUpProviderWrapper = React.lazy(() => import('./pages/SignUp'));
// Lazy-loaded: its chat input brings photo EXIF parsing, which nothing else at startup needs.
const AutomationsPage = React.lazy(() => import('./pages/Automations'));
// Used to inform Sentry about :params in the URLs. See:
// https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/#parameterized-transaction-names
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

// Route components
const SigninComponent: React.FC = () => {
  React.useEffect(() => {
    routeUtils.redirectToLoginApp(loginAppUrl);
  }, []);
  return <div />;
};

const SignupRoleKeyComponent: React.FC = () => {
  const {role, key} = useParams<{role: string; key?: string}>();
  React.useEffect(() => {
    if (role) {
      window.location.assign(`${loginAppUrl}/signup/${role}${key ? `/${key}` : ''}`);
    }
  }, [role, key]);
  return null;
};

const SignupComponent: React.FC = () => {
  const location = useLocation();
  const params = queryString.parse(location.search, {arrayFormat: 'comma'});
  const defaultFormState = routeUtils.parseSignUpQueryParams(params);
  return (
    <React.Suspense fallback={<Loading />}>
      <NewCustomerSignUpProviderWrapper initialFormState={defaultFormState} />
    </React.Suspense>
  );
};

const ForgotPasswordComponent: React.FC = () => {
  React.useEffect(() => {
    window.location.assign(`${loginAppUrl}/forgotPassword`);
  }, []);
  return null;
};

const WelcomeComponent: React.FC = () => {
  const location = useLocation();
  const redirectUrl = location.search.split('?')[1];
  return (
    <WithLoggedInAuth>
      {(_auth, profile, {currentOrganization}, loggedInUserActions) => (
        <OnboardingSlidesView
          redirectUrl={redirectUrl}
          organization={currentOrganization}
          profile={profile}
          loggedInUserActions={loggedInUserActions}
        />
      )}
    </WithLoggedInAuth>
  );
};

const ProjectsRedirectComponent: React.FC = () => {
  const location = useLocation();
  return (
    <WithLoggedInAuth>
      {(auth, _profile, {currentOrganization, profileOrganizations}) => {
        if (userUtils.shouldSeeOrgSwitcher(currentOrganization, profileOrganizations)) {
          return <Navigate to="/organizations" replace />;
        }
        const redirectTo = `/${routeUtils.makeUuidPrefix(auth.currentOrganization!.get('id'))}${
          location.pathname
        }${location.hash || ''}${location.search || ''}`;
        return <Navigate to={redirectTo} replace />;
      }}
    </WithLoggedInAuth>
  );
};

const ProjectDashboardComponent: React.FC = () => {
  const {currentOrgIdPrefix, selectedProjectId, page, featureIds} = useParams<{
    currentOrgIdPrefix: string;
    selectedProjectId: string;
    page?: string;
    featureIds?: string;
  }>();
  const pageValue =
    page === 'table' || page === 'map' || page === 'documents' || page === 'shareLinks'
      ? page
      : undefined;
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={currentOrgIdPrefix!}>
      <WithLoggedInAuth>
        {(auth, profile, {currentOrganization}, loggedInUserActions) => (
          <ProjectDashboardSwitch
            profile={profile}
            organization={currentOrganization}
            loggedInUserActions={loggedInUserActions}
            firebaseToken={auth.firebaseToken}
            selectedProjectIdParam={selectedProjectId!}
            selectedFeatureIdsParam={featureIds || null}
            page={pageValue}
          />
        )}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

const ProjectsHomeComponent: React.FC = () => {
  const {currentOrgIdPrefix} = useParams<{currentOrgIdPrefix: string}>();
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={currentOrgIdPrefix!}>
      <WithLoggedInAuth>
        {(_auth, profile, {currentOrganization}, loggedInUserActions) => (
          <LensHome
            organization={currentOrganization}
            profile={profile}
            loggedInUserActions={loggedInUserActions}
          />
        )}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

const AgentWorkflowComponent: React.FC = () => {
  const {currentOrgIdPrefix, workflowId, runId} = useParams<{
    currentOrgIdPrefix: string;
    workflowId: string;
    runs?: string;
    runId?: string;
  }>();
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={currentOrgIdPrefix!}>
      <WithLoggedInAuth>
        {(auth, profile, {currentOrganization}, loggedInUserActions) => {
          if (!featureFlags.AGENT(currentOrganization, profile)) {
            return <Navigate to="/projects" replace />;
          }
          return (
            <WorkflowsProvider organization={currentOrganization.toJS()}>
              <AgentWorkflowSwitch
                workflowId={workflowId!}
                runId={runId || undefined}
                auth={auth}
                profile={profile}
                organization={currentOrganization}
                loggedInUserActions={loggedInUserActions}
              />
            </WorkflowsProvider>
          );
        }}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

const CartComponent: React.FC = () => {
  const {currentOrgIdPrefix} = useParams<{currentOrgIdPrefix: string}>();
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={currentOrgIdPrefix!}>
      <WithLoggedInAuth>
        {(auth, profile, {currentOrganization}, loggedInUserActions) => {
          if (!featureFlags.IMAGERY_CART(currentOrganization, profile)) {
            return <Navigate to="/projects" replace />;
          }
          return (
            <RequireRoleContainer profile={profile} roles={['owner', 'admin']}>
              <LayoutWrapper
                auth={auth}
                profile={profile}
                organization={currentOrganization}
                loggedInUserActions={loggedInUserActions}
              >
                <CartPage />
              </LayoutWrapper>
            </RequireRoleContainer>
          );
        }}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

const AgentHomeComponent: React.FC = () => {
  const {currentOrgIdPrefix} = useParams<{currentOrgIdPrefix: string}>();
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={currentOrgIdPrefix!}>
      <WithLoggedInAuth>
        {(auth, profile, {currentOrganization}, loggedInUserActions) => {
          if (!featureFlags.AGENT(currentOrganization, profile)) {
            return <Navigate to="/projects" replace />;
          }
          return (
            <WorkflowsProvider organization={currentOrganization.toJS()}>
              <WorkflowsPage
                auth={auth}
                profile={profile}
                organization={currentOrganization}
                loggedInUserActions={loggedInUserActions}
              />
            </WorkflowsProvider>
          );
        }}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

const AutomationsComponent: React.FC = () => {
  const {currentOrgIdPrefix} = useParams<{currentOrgIdPrefix: string}>();
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={currentOrgIdPrefix!}>
      <WithLoggedInAuth>
        {(auth, profile, {currentOrganization}, loggedInUserActions) => {
          if (!featureFlags.AUTOMATIONS(currentOrganization, profile)) {
            return <Navigate to="/projects" replace />;
          }
          return (
            <React.Suspense fallback={<Loading />}>
              <AutomationsPage
                auth={auth}
                profile={profile}
                organization={currentOrganization}
                loggedInUserActions={loggedInUserActions}
              />
            </React.Suspense>
          );
        }}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

const AgentChatComponent: React.FC = () => {
  const {currentOrgIdPrefix} = useParams<{currentOrgIdPrefix: string}>();
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={currentOrgIdPrefix!}>
      <WithLoggedInAuth>
        {(auth, profile, {currentOrganization}, loggedInUserActions) => {
          if (!featureFlags.AGENT_CHAT(currentOrganization, profile)) {
            return <Navigate to="/projects" replace />;
          }
          return (
            <AgentChatPage
              auth={auth}
              profile={profile}
              organization={currentOrganization}
              loggedInUserActions={loggedInUserActions}
            />
          );
        }}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

const SettingsRedirectComponent: React.FC = () => {
  const location = useLocation();
  return (
    <WithLoggedInAuth>
      {(_auth, _profile, {currentOrganization}) => {
        const orgToView = currentOrganization;
        const redirectParts = [
          routeUtils.makeUuidPrefix(orgToView.get('id')),
          location.pathname.slice(1),
          location.hash || '',
          location.search || '',
        ].filter((p) => p);
        return <Navigate to={`/${redirectParts.join('/')}`} replace />;
      }}
    </WithLoggedInAuth>
  );
};

export const SettingsComponent: React.FC = () => {
  const {orgIdPrefix, activeView} = useParams<{orgIdPrefix: string; activeView?: string}>();
  const view = activeView || 'organization';
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={orgIdPrefix!}>
      <WithLoggedInAuth>
        {(_auth, profile, {currentOrganization}, loggedInUserActions) => {
          if (isLensPlaygroundUser(profile)) {
            return <Navigate to="/projects" replace />;
          }
          const canOpenView =
            isGlobalSettingsView(view) &&
            (view !== 'chat' || featureFlags.AGENT_CHAT(currentOrganization, profile));
          return canOpenView ? (
            <GlobalSettings
              organization={currentOrganization}
              profile={profile}
              loggedInUserActions={loggedInUserActions}
              activeView={view}
            />
          ) : (
            <Navigate to="/projects" replace />
          );
        }}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

const DashboardComponent: React.FC = () => {
  const {orgIdPrefix} = useParams<{orgIdPrefix: string}>();
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={orgIdPrefix!}>
      <WithLoggedInAuth>
        {(_auth, profile, {currentOrganization}, loggedInUserActions) => (
          <Dashboard
            organization={currentOrganization}
            profile={profile}
            loggedInUserActions={loggedInUserActions}
          />
        )}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

const ManagePropertiesRedirectComponent: React.FC = () => {
  const {mode, projectIdPrefix} = useParams<{mode?: string; projectIdPrefix?: string}>();
  return (
    <WithLoggedInAuth>
      {(_auth, __, {currentOrganization}) => {
        const orgIdPrefix = routeUtils.makeUuidPrefix(currentOrganization.get('id'));
        const destination = `/${orgIdPrefix}/admin/manageProperties${
          mode ? `/${mode}` : ''
        }${projectIdPrefix ? `/${projectIdPrefix}` : ''}`;
        return <Navigate to={destination} replace />;
      }}
    </WithLoggedInAuth>
  );
};

const ManagePropertiesComponent: React.FC = () => {
  const {orgIdPrefix, mode, projectId} = useParams<{
    orgIdPrefix: string;
    mode?: string;
    projectId?: string;
  }>();
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={orgIdPrefix!}>
      <WithLoggedInAuth>
        {(auth, profile, {currentOrganization}, loggedInUserActions) => {
          if (!mode || !isManagePropertyModeOption(mode)) {
            return (
              <Navigate
                to={`/${orgIdPrefix}/admin/manageProperties/select/${mode || ''}`}
                replace
              />
            );
          } else {
            return (
              <RequireRoleContainer profile={profile} roles={['owner', 'admin', 'regular']}>
                <ManageFeatures
                  profile={profile}
                  organization={currentOrganization}
                  loggedInUserActions={loggedInUserActions}
                  projectId={projectId}
                  mode={mode as ManagePropertiesMode}
                  firebaseToken={auth.firebaseToken}
                />
              </RequireRoleContainer>
            );
          }
        }}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

const ManagePropertiesHomeComponent: React.FC = () => {
  const {orgIdPrefix} = useParams<{orgIdPrefix: string}>();
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={orgIdPrefix!}>
      <WithLoggedInAuth>
        {(_auth, profile, {currentOrganization}, loggedInUserActions) => (
          <ManageFeatures
            profile={profile}
            organization={currentOrganization}
            loggedInUserActions={loggedInUserActions}
            mode={'select'}
            firebaseToken={null}
          />
        )}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

const UpdatePropertiesRedirectComponent: React.FC = () => {
  const {projectIdPrefix} = useParams<{projectIdPrefix: string}>();
  return (
    <WithLoggedInAuth>
      {(_auth, __, {currentOrganization}) => {
        const orgIdPrefix = routeUtils.makeUuidPrefix(currentOrganization.get('id'));
        return (
          <Navigate
            to={`/${orgIdPrefix}/admin/updateProperties${
              projectIdPrefix ? `/${projectIdPrefix}` : ''
            }`}
            replace
          />
        );
      }}
    </WithLoggedInAuth>
  );
};

const UpdatePropertiesComponent: React.FC = () => {
  const {orgIdPrefix, projectId} = useParams<{orgIdPrefix: string; projectId: string}>();
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={orgIdPrefix!}>
      <WithLoggedInAuth>
        {(_auth, profile, {currentOrganization}, loggedInUserActions) => (
          <RequireRoleContainer profile={profile} roles={['owner', 'admin', 'regular']}>
            <PropertyUpdate
              organization={currentOrganization}
              profile={profile}
              loggedInUserActions={loggedInUserActions}
              projectId={projectId!}
            />
          </RequireRoleContainer>
        )}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

const PublicWidgetComponent: React.FC = () => {
  const {uuid} = useParams<{uuid: string}>();
  return <PublicWidget uuid={uuid!} />;
};

const PublicReportComponent: React.FC = () => {
  const {uuid} = useParams<{uuid: string}>();
  return <PublicReportPage uuid={uuid!} />;
};

const PublicStoryComponent: React.FC = () => {
  const {uuid} = useParams<{uuid: string}>();
  return <PublicStoryPage uuid={uuid!} />;
};

const PublicMapComponent: React.FC = () => {
  const {uuid} = useParams<{uuid: string}>();
  return <PublicMap uuid={uuid!} />;
};

const StoryViewComponent: React.FC = () => {
  const {currentOrgIdPrefix, selectedProjectId, featureId, reportId} = useParams<{
    currentOrgIdPrefix: string;
    selectedProjectId: string;
    featureId: string;
    reportId: string;
  }>();
  return (
    <routeUtils.CurrentOrgIdPrefix.Provider value={currentOrgIdPrefix!}>
      <WithLoggedInAuth>
        {(auth, _profile, {currentOrganization}) => (
          <StoryViewPage
            organization={currentOrganization}
            firebaseToken={auth.firebaseToken}
            projectIdParam={selectedProjectId!}
            featureIdParam={featureId!}
            reportId={parseInt(reportId!, 10)}
          />
        )}
      </WithLoggedInAuth>
    </routeUtils.CurrentOrgIdPrefix.Provider>
  );
};

export const AppRouter: React.FC = () => {
  return (
    <>
      <ScrollOnRouteChange />

      <SentryRoutes>
        <Route path="/error" element={<ErrorPages />} />

        <Route
          path="/signin"
          element={<WithLoggedOutAuth>{() => <SigninComponent />}</WithLoggedOutAuth>}
        />

        <Route path="/signout" element={<WithAuth>{(auth) => <SignOut auth={auth} />}</WithAuth>} />

        <Route path="/signup/:role/:key?" element={<SignupRoleKeyComponent />} />

        <Route path="/signup" element={<SignupComponent />} />

        <Route path="/signupSuccess" element={<NewCustomerSignUpSuccess />} />

        <Route
          path="/discourse/sso"
          element={
            <WithLoggedInAuth>
              {(auth) => {
                const params = queryString.parse(window.location.search);
                params.apiToken = auth.firebaseToken;
                const url = getApiRoute('user/discourse', params);
                // Use window.location.href not <Navigate> since we're leaving this app
                window.location.href = url;
                return <div />; // unused, need to return something to satisfy the type checker
              }}
            </WithLoggedInAuth>
          }
        />

        <Route
          path="/sso/login"
          element={
            <WithLoggedInAuth>
              {(auth) => {
                // Use window.location.href not <Navigate> since we're leaving this app
                const url = getApiRoute('saml/login/process', {apiToken: auth.firebaseToken});
                window.location.href = url;
                return <div />; // unused, need to return something to satisfy the type checker
              }}
            </WithLoggedInAuth>
          }
        />

        <Route path="/forgotPassword" element={<ForgotPasswordComponent />} />

        <Route path="/welcome" element={<WelcomeComponent />} />

        <Route path="/projects" element={<ProjectsRedirectComponent />} />

        <Route path={routeUtils.STORY_ROUTE_PATH} element={<StoryViewComponent />} />

        <Route
          path="/:currentOrgIdPrefix/projects/:selectedProjectId/:page?/:featureIds?"
          element={<ProjectDashboardComponent />}
        />

        <Route path="/:currentOrgIdPrefix/projects" element={<ProjectsHomeComponent />} />

        <Route
          path="/:currentOrgIdPrefix/agent/workflows/:workflowId/:runs?/:runId?"
          element={<AgentWorkflowComponent />}
        />

        <Route path="/:currentOrgIdPrefix/agent" element={<AgentHomeComponent />} />

        <Route path="/:currentOrgIdPrefix/automations" element={<AutomationsComponent />} />

        <Route path="/:currentOrgIdPrefix/cart" element={<CartComponent />} />

        <Route path="/:currentOrgIdPrefix/chat" element={<AgentChatComponent />} />

        <Route
          path="/organizations"
          element={
            <WithLoggedInAuth>
              {(_, profile, {currentOrganization, profileOrganizations}, loggedInUserActions) => {
                return userUtils.shouldSeeOrgSwitcher(currentOrganization, profileOrganizations) ? (
                  <OrganizationSwitch
                    profile={profile}
                    currentOrganization={currentOrganization}
                    profileOrganizations={profileOrganizations}
                    loggedInUserActions={loggedInUserActions}
                  />
                ) : (
                  <Navigate to="/projects" replace />
                );
              }}
            </WithLoggedInAuth>
          }
        />

        <Route path="/settings" element={<SettingsRedirectComponent />} />

        <Route path="/:orgIdPrefix/settings/:activeView?" element={<SettingsComponent />} />

        <Route path="/:orgIdPrefix/dashboard" element={<DashboardComponent />} />

        <Route
          path="/admin/manageProperties/:mode?/:projectIdPrefix?"
          element={<ManagePropertiesRedirectComponent />}
        />

        <Route
          path="/:orgIdPrefix/admin/manageProperties/:mode/:projectId?"
          element={<ManagePropertiesComponent />}
        />

        <Route
          path="/:orgIdPrefix/admin/manageProperties"
          element={<ManagePropertiesHomeComponent />}
        />

        <Route
          path="/admin/updateProperties/:projectIdPrefix"
          element={<UpdatePropertiesRedirectComponent />}
        />

        <Route
          path="/:orgIdPrefix/admin/updateProperties/:projectId"
          element={<UpdatePropertiesComponent />}
        />

        <Route
          path="/:orgIdPrefix/admin/updateProperties"
          element={<Navigate to="/projects" replace />}
        />

        <Route
          path="/internal-tools"
          element={
            <WithLoggedInAuth>
              {(auth, profile, {currentOrganization}, loggedInUserActions) =>
                userUtils.showInternalTools(profile, currentOrganization) ? (
                  <InternalTools
                    organization={currentOrganization}
                    profile={profile}
                    firebaseIsEmailVerified={auth.firebaseIsEmailVerified}
                    loggedInUserActions={loggedInUserActions}
                  />
                ) : (
                  <Navigate to="/projects" replace />
                )
              }
            </WithLoggedInAuth>
          }
        />

        <Route path="/p/w/:uuid" element={<PublicWidgetComponent />} />

        <Route path="/p/r/:uuid" element={<PublicReportComponent />} />

        <Route path="/p/s/:uuid" element={<PublicStoryComponent />} />

        <Route path="/p/:uuid" element={<PublicMapComponent />} />

        <Route path="/" element={<Navigate to="/signin" replace />} />
      </SentryRoutes>
    </>
  );
};

export default AppRouter;
